import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, Stack } from '@mui/material';
import { translateKey } from 'src/locales/translate';

interface MedicationFormActionsProps {
  /** An existing medication opens as a summary; editing it is a deliberate act. */
  isPreview: boolean;
  onStartEditing: () => void;
  activeStep: number;
  isLastStep: boolean;
  onNext: () => void;
  onBack: () => void;
  closeModal: () => void;
  onDelete?: () => void;
  canEdit: boolean;
  /** What the summary's edit button says; "Edytuj" unless given. */
  editLabel?: string;
  isEditing: boolean;
  showDeleteButton: boolean;
  isCreating?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

/** Close or back, then delete, on the left; edit / next / save on the right. */
export default function MedicationFormActions({
  isPreview,
  onStartEditing,
  activeStep,
  isLastStep,
  onNext,
  onBack,
  closeModal,
  onDelete,
  canEdit,
  editLabel,
  isEditing,
  showDeleteButton,
  isCreating,
  isUpdating,
  isDeleting,
}: MedicationFormActionsProps) {
  const showDelete = (isEditing || showDeleteButton) && canEdit && !!onDelete;
  // Editing has somewhere to go back to from its first step: the summary.
  const showBack = !isPreview && (activeStep > 0 || isEditing);

  return (
    <DialogActions
      sx={{ justifyContent: 'space-between', alignItems: 'center' }}
    >
      {/* As the visit booking: Zamknij on the first step, Wstecz after it. */}
      <Stack sx={{ flexDirection: 'row', gap: 1.5 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={showBack ? onBack : closeModal}
          data-testid={showBack ? 'medication-back' : 'medication-cancel'}
        >
          {showBack
            ? translateKey('quickBooking.back')
            : translateKey('common.close')}
        </Button>

        {showDelete && (
          <LoadingButton
            color="error"
            variant="contained"
            data-testid="medication-delete-open"
            onClick={onDelete}
            disabled={isDeleting || isUpdating}
          >
            {translateKey('common.remove')}
          </LoadingButton>
        )}
      </Stack>

      <Stack sx={{ flexDirection: 'row', gap: 1.5 }}>
        {/* Reading first, changing second: the summary is what opens, and the
            form behind it is a click away. */}
        {isPreview && canEdit && (
          <Button
            variant="contained"
            onClick={onStartEditing}
            data-testid="medication-edit-start"
          >
            {editLabel ?? translateKey('visit.editAction')}
          </Button>
        )}

        {!isPreview && !isLastStep && (
          <Button
            variant="contained"
            onClick={onNext}
            data-testid="medication-next"
          >
            {translateKey('quickBooking.next')}
          </Button>
        )}

        {/* Saving belongs to the summary, which is the last step: the vet
            confirms what they are about to write, then writes it. */}
        {!isPreview && canEdit && isLastStep && (
          <LoadingButton
            type="submit"
            variant="contained"
            data-testid="medication-save"
            disabled={isUpdating || isCreating || isDeleting}
            loading={isUpdating || isCreating}
          >
            {isEditing || showDeleteButton
              ? translateKey('common.save')
              : translateKey('common.add')}
          </LoadingButton>
        )}
      </Stack>
    </DialogActions>
  );
}
